import React, { ReactNode } from "react"
import {
  Grid, Text, Box, HStack,
} from "@chakra-ui/react"

type PropertyRowProps = {
  label: string
  children: ReactNode
  /**
   * If true, children will be wrapped in HStack with justifyContent="flex-end"
   * Use this for multiple small inputs (e.g., X/Y, W/H, or action buttons)
   * If false, children take full width of the right column
   */
  rightAlign?: boolean
  /**
   * Gap between children when rightAlign is true
   */
  childrenGap?: number
}

/**
 * A row of an editor's property panel: a label and its controls
 *
 * Layout: 80px label + flexible input area with 16px gap
 *
 * @example
 * // Full-width input (default)
 * <PropertyRow label="Border">
 *   <Button>...</Button>
 * </PropertyRow>
 *
 * @example
 * // Right-aligned compact inputs
 * <PropertyRow label="Position" rightAlign>
 *   <NumberField name="x" ... />
 *   <NumberField name="y" ... />
 * </PropertyRow>
 */
const PropertyRow = ({
  label,
  children,
  rightAlign = false,
  childrenGap = 2,
}: PropertyRowProps) => (
  <Grid templateColumns="80px 1fr" alignItems="center" gap={4} w="full">
    <Text fontSize="sm">{label}</Text>
    {rightAlign ? (
      <HStack gap={childrenGap} justifyContent="flex-end">
        {children}
      </HStack>
    ) : (
      <Box>{children}</Box>
    )}
  </Grid>
)

export default PropertyRow
